import React = require("react");
import * as Dropdown from "OfficeFabric/Dropdown";
import * as AgileProductBacklogResources from "Agile/Scripts/Resources/TFS.Resources.AgileProductBacklog";
import { ITeam } from "Agile/Scripts/Models/Team";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import { LoadingComponent } from "Presentation/Scripts/TFS/Components/LoadingComponent";
import * as Tooltip from "VSSUI/Tooltip";
import { WorkItemTypeColorAndIconsProvider } from "Presentation/Scripts/TFS/FeatureRef/WorkItemTypeColorAndIconsProvider";
import { getDefaultWebContext } from "VSS/Context";
import { WorkItemLink, WorkItem } from "TFS/WorkItemTracking/Contracts";
import { BacklogLevelWorkItems } from "TFS/Work/Contracts";
import { getClient } from "VSS/Service";
import { WorkHttpClient } from "TFS/Work/RestClient";
import { PageWorkItemHelper } from "WorkItemTracking/Scripts/Utils/PageWorkItemHelper";
import { CoreFieldRefNames } from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";
import { format } from "VSS/Utils/String";

interface IParentPickerState {
    isLoading: boolean;
    ids: number[];
    workItemMap: { [id: number]: WorkItem };
    errorMessage?: string;
}

interface IParentPickerProps {
    projectId: string;
    selectedTeam: ITeam;
    parentBacklogLevelId: string;
    selectedParentId?: number;
    setSelectedParentId: (id?: number) => void;
}

export const ParentPicker = (props: IParentPickerProps) => {
    const [parentsState, setParentsState] = React.useState({ isLoading: false, ids: [], workItemMap: {} } as IParentPickerState);

    React.useEffect(() => {
        let isCancelled = false;
        (async () => {
            const { projectId, selectedTeam, selectedParentId, setSelectedParentId } = props;

            setParentsState({ ...parentsState, isLoading: true });
            const teamContext = {
                team: selectedTeam.name,
                teamId: selectedTeam.id,
                project: projectId,
                projectId: projectId
            };

            try {
                const workRestClient = getClient(WorkHttpClient, undefined, undefined, undefined, undefined);
                const backlogWorkItems: BacklogLevelWorkItems = await workRestClient.getBacklogLevelWorkItems(
                    teamContext,
                    props.parentBacklogLevelId
                );
                if (backlogWorkItems && backlogWorkItems.workItems && !isCancelled) {
                    const workItemIds = backlogWorkItems.workItems.map((workItemLink: WorkItemLink) => workItemLink.target.id);

                    const fieldRefNames = [CoreFieldRefNames.TeamProject, CoreFieldRefNames.Title, CoreFieldRefNames.WorkItemType];
                    const workItems: WorkItem[] = await PageWorkItemHelper.pageWorkItems(workItemIds, /* projectName */ null, fieldRefNames);

                    // Stop if cancelled
                    if (isCancelled) {
                        return;
                    }
                    // Filter out ids not in response, but keep in order provided by getBacklogLevelWorkItems
                    const validIds = workItemIds.filter(id => workItems.some(workItem => workItem.id === id));

                    setSelectedParentId(validIds.length > 0 ? validIds[0] : selectedParentId);
                    setParentsState({
                        isLoading: false,
                        ids: validIds,
                        workItemMap: workItems.reduce((map, workItem) => {
                            map[workItem.id] = workItem;
                            return map;
                        }, {})
                    });
                }
            } catch (error) {
                setParentsState({
                    isLoading: false,
                    ids: [],
                    workItemMap: {},
                    errorMessage: format(AgileProductBacklogResources.ChangeParentErrorLoadingParentWorkItemsMessage, error.message)
                });
            }
        })();
        return () => {
            isCancelled = true; // Cancel existing request if we start fetching something else
        };
    }, [props.projectId, props.selectedTeam, props.parentBacklogLevelId]);

    const render = (): JSX.Element => {
        const { selectedTeam, selectedParentId, setSelectedParentId } = props;

        if (parentsState.isLoading) {
            return <LoadingComponent />;
        }

        if (parentsState.errorMessage) {
            return <div>{parentsState.errorMessage}</div>;
        }

        const dropdownProps: Dropdown.IDropdownProps = {
            label: (
                <span className="ms-font-m ms-fontColor-neutralSecondary ms-fontWeight-semibold">
                    {AgileProductBacklogResources.ChangeParentDialogWorkItemPickerLabel}
                </span>
            ) as any,
            onChanged: (option: Dropdown.IDropdownOption) => {
                const id = option.key as number;
                if (id !== selectedParentId) {
                    setSelectedParentId(id);
                }
            },
            onRenderTitle: (): JSX.Element => {
                if (selectedParentId) {
                    return _renderWorkItem(selectedParentId, "selection-title");
                }
                const workItemsAvailable = parentsState.ids.length > 0;
                if (workItemsAvailable) {
                    return <div>{AgileProductBacklogResources.ChangeParentDialogWorkItemPickerHelpText}</div>;
                } else {
                    return <div>{AgileProductBacklogResources.ChangeParentDialogWorkItemPickerDisabledText}</div>;
                }
            },
            onRenderOption: (option: Dropdown.IDropdownOption): JSX.Element => {
                return _renderWorkItem(option.key as number, "dropdown-title");
            },
            disabled: false,
            ariaLabel: AgileProductBacklogResources.ChangeParentDialogWorkItemPickerLabel
        };

        if (parentsState.ids.length > 0) {
            dropdownProps.options = parentsState.ids.map(id => {
                return { key: id, selected: Number(id) === selectedParentId } as Dropdown.IDropdownOption;
            });
        } else {
            dropdownProps.disabled = true;
        }

        return (
            <div className="parent-work-item-picker" key={`parent-work-item-picker-${selectedTeam.id}`}>
                <Dropdown.Dropdown {...dropdownProps} />
            </div>
        );
    };

    const _renderWorkItem = (workItemId: number, parentClass?: string): JSX.Element => {
        const workItem: WorkItem = parentsState.workItemMap[workItemId];
        const teamProject = workItem.fields[CoreFieldRefNames.TeamProject] || getDefaultWebContext().project.name;
        const workItemTypeIconDetails = WorkItemTypeColorAndIconsProvider.getInstance().getColorAndIcon(
            teamProject,
            workItem.fields[CoreFieldRefNames.WorkItemType]
        );
        const title = workItem.fields[CoreFieldRefNames.Title];

        return (
            <div className={`change-parent-workitem-picker ${parentClass || ""}`}>
                <VssIcon
                    className="work-item-type-icon"
                    iconType={VssIconType.bowtie}
                    iconName={workItemTypeIconDetails.icon}
                    styles={{ root: { color: workItemTypeIconDetails.color } }}
                />
                <Tooltip.TooltipHost overflowMode={Tooltip.TooltipOverflowMode.Parent} content={title}>
                    <div className="work-item-id">{`${workItem.id}:`}</div>
                    {title}
                </Tooltip.TooltipHost>
            </div>
        );
    };

    return render();
};
